import { Dtos, IndividualContactId, SuretyAccountId } from "@inrev/common";
import {
	useDeleteAccountIndividual,
	useFetchAccountIndividuals,
	useFetchSuretyAccount,
	useSaveAccountIndividual,
} from "@portal/api/account";
import { IndividualContactCard } from "@portal/components/features/contact-cards";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { Text } from "@portal/components/primitives";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { FC, useMemo, useState } from "react";
import { formatName } from "../../../../../utils";
import { SectionHeaderWithAdd } from "../../../../account/admin/tabs/details/SectionHeaderWithAdd";
import { AccountIndividualModal } from "../../create/components/AccountIndividualModal";
import { createDefaultOwner, OwnerFormData, SpouseFormData } from "../../create/schema";

type AccountIndividualsSectionProps = {
	accountId: SuretyAccountId;
};

export const AccountIndividualsSection: FC<AccountIndividualsSectionProps> = ({ accountId }) => {
	const { suretyAccount, suretyAccountIsLoading, suretyAccountError, refetchSuretyAccount } =
		useFetchSuretyAccount(accountId);
	const { individuals, individualsIsLoading, individualsError, refetchAccountIndividuals } =
		useFetchAccountIndividuals(accountId);

	const { saveAccountIndividual } = useSaveAccountIndividual(accountId);
	const { deleteAccountIndividual } = useDeleteAccountIndividual(accountId);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingIndividualId, setEditingIndividualId] = useState<IndividualContactId | null>(null);

	const editingData = useMemo(() => {
		if (!editingIndividualId) return { owner: undefined, spouse: undefined };
		const individualsList = individuals || [];
		const owner = individualsList.find((ind) => ind.id === editingIndividualId);

		let spouse: Dtos.Individual.Get.Response | undefined;
		if (owner?.spouseId) {
			spouse = individualsList.find((ind) => ind.id === owner.spouseId);
		}

		return { owner, spouse };
	}, [editingIndividualId, individuals]);

	if (suretyAccountIsLoading || individualsIsLoading) {
		return (
			<div className="h-[400px]">
				<CenteredSpinner />
			</div>
		);
	}

	if (suretyAccountError) {
		return (
			<ReportedRetryableLoadError
				error={suretyAccountError}
				resource="account"
				onRetry={refetchSuretyAccount}
			/>
		);
	}

	if (!suretyAccount) {
		return <CenteredSpinner />;
	}

	if (individualsError) {
		return (
			<ReportedRetryableLoadError
				error={individualsError}
				resource="account individuals"
				onRetry={refetchAccountIndividuals}
			/>
		);
	}

	const isReadOnly = suretyAccount.status === "closed" || !!suretyAccount.indemnityAgreementId;
	const individualsList = individuals || [];

	const findSpouse = (individual: Dtos.Individual.Get.Response) => {
		if (!individual.spouseId) return undefined;
		return individualsList.find((ind) => ind.id === individual.spouseId);
	};

	const handleAddIndividual = () => {
		setEditingIndividualId(null);
		setIsModalOpen(true);
	};

	const resolveOwnerId = (individualId: IndividualContactId): IndividualContactId => {
		const individual = individualsList.find((ind) => ind.id === individualId);
		if (individual && individual.type !== "owner") {
			const owner = individualsList.find((ind) => ind.spouseId === individualId);
			if (owner) return owner.id;
		}
		return individualId;
	};

	const handleEditIndividual = (individualId: IndividualContactId) => {
		setEditingIndividualId(resolveOwnerId(individualId));
		setIsModalOpen(true);
	};

	const handleDeleteIndividual = (individualId: IndividualContactId) => {
		deleteAccountIndividual({ individualId });
	};

	const toIndividualData = (data: OwnerFormData | SpouseFormData) => ({
		firstName: data.firstName,
		middleInitial: data.middleInitial,
		lastName: data.lastName,
		suffix: data.suffix,
		address: data.address,
		email: data.email,
		phone: data.phone,
		ssn: "ssn" in data ? data.ssn : undefined,
		ownsAHome: "ownsAHome" in data ? data.ownsAHome : undefined,
		type: data.type,
		creditReportPermission: data.creditReportPermission,
	});

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setEditingIndividualId(null);
	};

	const handleSaveIndividual = (
		owner: OwnerFormData,
		spouse: OwnerFormData | SpouseFormData | null,
	) => {
		const existingSpouseId = editingIndividualId
			? individualsList.find((i) => i.id === editingIndividualId)?.spouseId
			: undefined;

		saveAccountIndividual(
			{
				editingIndividualId,
				existingSpouseId,
				ownerData: toIndividualData(owner),
				spouseData: spouse ? toIndividualData(spouse) : null,
			},
			{ onSuccess: handleCloseModal },
		);
	};

	const partialActive = suretyAccount.status === "review";

	return (
		<>
			<div
				data-testid="agent-account-individuals-section"
				className="w-full flex flex-col space-y-5"
			>
				{isReadOnly ? (
					<Text type="heading" color="primary">
						Individuals
					</Text>
				) : (
					<SectionHeaderWithAdd
						title="Individuals"
						addLabel="Add Individual"
						testId="add-individual"
						onAdd={handleAddIndividual}
					/>
				)}
				<div className="w-full flex flex-wrap gap-[15px]">
					{individualsList.length === 0 ? (
						<Text type="body" color="secondary">
							None
						</Text>
					) : (
						individualsList.map((individual) => {
							const spouse = findSpouse(individual);
							const spouseName = spouse ? formatName(spouse) : undefined;
							const spouseIsOwner = spouse?.type === "owner";
							return (
								<IndividualContactCard
									key={individual.id}
									individual={individual}
									spouseName={spouseName}
									statusCells={[
										{ label: "Owner", active: individual.type === "owner" },
										{ label: "Indemnifies", active: !!individual.includeInIndemnity },
										{
											label: "Credit Permitted",
											active: !!individual.creditReportPermission,
										},
									]}
									onEdit={isReadOnly ? undefined : () => handleEditIndividual(individual.id)}
									onDelete={isReadOnly ? undefined : () => handleDeleteIndividual(individual.id)}
									deleteSubMessage={
										individual.type === "owner" && spouseName && !spouseIsOwner
											? `This will also delete their spouse, ${spouseName}.`
											: undefined
									}
									partialActive={partialActive}
								/>
							);
						})
					)}
				</div>
			</div>

			{isModalOpen && (
				<AccountIndividualModal
					isOpen={isModalOpen}
					onClose={handleCloseModal}
					onSave={handleSaveIndividual}
					showCreditCheck={true}
					initialOwner={
						editingData.owner
							? {
									firstName: editingData.owner.firstName,
									middleInitial: editingData.owner.middleInitial ?? null,
									lastName: editingData.owner.lastName,
									suffix: editingData.owner.suffix ?? null,
									address: editingData.owner.address,
									email: editingData.owner.email ?? null,
									phone: editingData.owner.phone ?? null,
									ssn: editingData.owner.ssn ?? null,
									ownsAHome: editingData.owner.ownsAHome ?? null,
									creditReportPermission: editingData.owner.creditReportPermission ?? false,
									type: "owner" as const,
								}
							: createDefaultOwner()
					}
					initialSpouse={
						editingData.spouse
							? {
									firstName: editingData.spouse.firstName,
									middleInitial: editingData.spouse.middleInitial ?? null,
									lastName: editingData.spouse.lastName,
									suffix: editingData.spouse.suffix ?? null,
									address: editingData.spouse.address,
									email: editingData.spouse.email ?? null,
									phone: editingData.spouse.phone ?? null,
									ssn: editingData.spouse.ssn ?? null,
									type: editingData.spouse.type ?? null,
									creditReportPermission: editingData.spouse.creditReportPermission ?? null,
								}
							: undefined
					}
				/>
			)}
		</>
	);
};
